import { useState } from "react";
import { DataManager, Predicate, Query } from "@syncfusion/ej2-data";
import React from "react";
import { useOData } from "../../../../stores/OData/useOData";
import { DefaultHtmlAttributes } from "../../../../";
import { resolveODataUrl } from "../types";
import { AsyncMultiSelectTreeFieldProps } from "./AsyncMultiSelectTreeField";
import {
  DropDownTreeComponent,
  DropDownTreeModel,
} from "@syncfusion/ej2-react-dropdowns";
import { AsyncMultiSelectTreeFieldValue, Option } from "../../types/fieldTypes";
export const useAsyncMultiSelectTreeField = (
  props: AsyncMultiSelectTreeFieldProps
) => {
  const {
    authRequestHeader,
    setupAsyncMultiselectODataSource,
    baseActionCompleteEvent: _,
    baseActionFailureEvent,
  } = useOData(
    {
      getAuthRequestHeader:
        props.getAuthRequestHeader || (() => Promise.resolve({})),
    },
    () => {
      setInitialized(true);
    }
  );

  const multiSelectRef = React.useRef<DropDownTreeComponent | null>(null);

  const [initialized, setInitialized] = React.useState<boolean>(false);
  const [dataSource, setDataSource] = useState<DataManager>();
  const [localOptions, setLocalOptions] = useState<Option<string>[]>([]);
  const [queryKey, setQueryKey] = React.useState<string>();
  const [multiselectTreeSettings, setMultiselectTreeSettings] = React.useState<
    DropDownTreeModel & DefaultHtmlAttributes
  >();
  React.useEffect(() => {
    setQueryKey("initial-key");
  }, []);

  /** Observe and Save Filter Settings */
  React.useEffect(() => {
    if (initialized) {
      setup();
    }
  }, [initialized, queryKey]);

  const getQuery = (queryString?: string) => {
    const predicates = [...(props.conditions || [])];

    if (queryString) {
      predicates.push(new Predicate("label", "contains", queryString, true));
    }

    let where: Predicate | undefined;

    if (predicates) {
      predicates.forEach((predicate) => {
        where = where ? where.and(predicate) : predicate;
      });
    }

    let query = new Query().select([
      "label",
      "value",
      ...(props.optionFields || []),
    ]);

    if (where) {
      query = query.where(where);
    }
    query = query.sortBy("label");
    return query;
  };

  const showSearchPopup = () => {
    if (!!multiSelectRef.current) {
      multiSelectRef.current.showPopup();
    }
  };

  const setNoRecordsTemplate = () => {
    if (!!multiSelectRef.current && !!props.noRecordsTemplate) {
      multiSelectRef.current.noRecordsTemplate = props.noRecordsTemplate(
        multiSelectRef.current
      ) as any;
    }
  };

  /**
   *
   * SyncFusion async select component has a terrible habit of direct
   * update the value of the input prop and set it to something like [null], ['']
   * in the process
   *
   */
  const sanitizeValue = (values: AsyncMultiSelectTreeFieldValue) => {
    return values?.filter((value) => value != null && value != "");
  };

  const defaultMultiselectSettings: DropDownTreeModel & DefaultHtmlAttributes =
    {
      showClearButton: true,
      /** Inmutable */
      focus: showSearchPopup,
      onClick: showSearchPopup,
      actionFailure: baseActionFailureEvent,
      placeholder: "Type to search...",
      disabled: props.disabled,
      allowFiltering: true,
      ignoreCase: true,
      sortOrder: "Ascending",
    };

  const setup = async () => {
    if (!queryKey) {
      return;
    }
    if (!!authRequestHeader) {
      // Setup Properties
      const settings = {
        ...defaultMultiselectSettings,
        ...{},
      } as DropDownTreeModel & DefaultHtmlAttributes;

      const resolvedODataUrl = resolveODataUrl(props);

      const dm = setupAsyncMultiselectODataSource(
        resolvedODataUrl,
        props.groupByActiveStatus
      );

      settings.fields = {
        dataSource: dm,
        query: getQuery(),
        text: "label",
        value: "value",
        parentValue: "parentValue",
        hasChildren: "value",
      };

      setDataSource(dm);
      settings.value = props.value;
      setMultiselectTreeSettings(settings);
    }
  };

  return {
    getQuery,
    dataSource,
    showSearchPopup,
    setNoRecordsTemplate,
    localOptions,
    setLocalOptions,
    multiSelectRef,
    sanitizeValue,
    multiselectTreeSettings,
  };
};
